import { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router';
import { FileText, Plus, Search } from 'lucide-react';
import { useInvoices, useSetInvoiceStatus } from '@/hooks/useInvoices';
import { money, moneyCompact, parseDay } from '@/lib/format';
import type { InvoiceSummary } from '@/types';
import { Screen } from '@m/ui/Screen';
import { Card, Group, Row } from '@m/ui/List';
import { BarButton, Button, Empty, SearchField, Segmented, SkeletonList } from '@m/ui/Controls';
import { SwipeRow } from '@m/ui/SwipeRow';
import { StatusPill, isOverdue } from '@m/ui/StatusPill';
import { cn } from '@m/ui/cn';
import { NewInvoiceSheet } from './NewInvoice';

type Filter = 'all' | 'draft' | 'sent' | 'paid' | 'overdue';
const FILTERS: Filter[] = ['all', 'draft', 'sent', 'paid', 'overdue'];

const SHORT = new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric' });
export const shortDay = (day: string | null | undefined) => (day ? SHORT.format(parseDay(day)) : '—');

/** "INV-1039 · due Sep 30", "· not sent", "· paid Oct 2". */
export function invoiceLine(inv: InvoiceSummary) {
  const when =
    inv.status === 'paid'
      ? `paid ${shortDay(inv.paidDate)}`
      : inv.status === 'draft' || inv.status === 'tentative'
        ? 'not sent'
        : `due ${shortDay(inv.dueDate)}`;
  return `${inv.invoiceNumber} · ${when}`;
}

const VISIBLE = new Set(['draft', 'sent', 'paid']);

/** Invoices (S06): what is owed up top, then every invoice by state. */
export function InvoicesScreen() {
  // ?filter=overdue is how Reports' Aging row (and the Overdue total) land here.
  const [params, setParams] = useSearchParams();
  const raw = params.get('filter') as Filter | null;
  const filter: Filter = raw && FILTERS.includes(raw) ? raw : 'all';
  const setFilter = (f: Filter) => setParams(f === 'all' ? {} : { filter: f }, { replace: true });
  const [searching, setSearching] = useState(false);
  const [search, setSearch] = useState('');
  const [creating, setCreating] = useState(false);
  const list = useInvoices({ status: filter, search, pageSize: 100 });
  // The totals are over every sent invoice, whatever the segment shows.
  const sent = useInvoices({ status: 'sent', pageSize: 100 });
  const setStatus = useSetInvoiceStatus();

  const items = (list.data?.items ?? []).filter((i) => VISIBLE.has(i.status));
  const totals = useMemo(() => {
    const s = sent.data?.items ?? [];
    return {
      outstanding: s.reduce((t, i) => t + i.totalAmount, 0),
      overdue: s.filter(isOverdue).reduce((t, i) => t + i.totalAmount, 0)
    };
  }, [sent.data]);

  return (
    <Screen
      title="Invoices"
      actions={
        <>
          <BarButton aria-label="Search invoices" onClick={() => setSearching((s) => !s)}>
            <Search className="size-[22px]" />
          </BarButton>
          <BarButton aria-label="New invoice" onClick={() => setCreating(true)}>
            <Plus className="size-[26px]" strokeWidth={2.2} />
          </BarButton>
        </>
      }
    >
      {searching && <SearchField value={search} onChange={setSearch} placeholder="Search invoices" autoFocus />}

      <Card className="grid grid-cols-2 gap-3 py-3.5">
        <div className="flex flex-col gap-0.5">
          <span className="text-caption font-medium text-[var(--text-muted)]">Outstanding</span>
          <span className="tnum text-[22px] leading-7 font-semibold">{moneyCompact(totals.outstanding)}</span>
        </div>
        <button type="button" onClick={() => setFilter('overdue')} className="flex flex-col gap-0.5 text-left active:opacity-60">
          <span className="text-caption font-medium text-[var(--text-muted)]">Overdue</span>
          <span className={cn('tnum text-[22px] leading-7 font-semibold', totals.overdue > 0 && 'text-orange-700 dark:text-orange-300')}>
            {moneyCompact(totals.overdue)}
          </span>
        </button>
      </Card>

      <Segmented
        value={filter === 'overdue' ? 'sent' : filter}
        onChange={setFilter}
        options={[
          { value: 'all', label: 'All' },
          { value: 'draft', label: 'Draft' },
          { value: 'sent', label: 'Sent' },
          { value: 'paid', label: 'Paid' }
        ]}
      />

      {list.isPending ? (
        <SkeletonList />
      ) : items.length === 0 ? (
        <Empty
          icon={<FileText />}
          title={search ? 'No matches' : filter === 'all' ? 'No invoices yet' : `No ${filter} invoices`}
          body={search ? undefined : 'Turn unbilled time into an invoice in three taps.'}
        >
          {!search && <Button onClick={() => setCreating(true)}>New invoice</Button>}
        </Empty>
      ) : (
        <Group>
          {items.map((inv) => {
            const overdue = isOverdue(inv);
            return (
              <SwipeRow
                key={inv.id}
                title={`${inv.invoiceNumber} · ${inv.clientName ?? ''}`}
                leading={
                  inv.status === 'sent'
                    ? { label: 'Mark paid', tone: 'accent', onSelect: () => setStatus.mutate({ id: inv.id, status: 'paid' }) }
                    : undefined
                }
              >
                <Row
                  to={`/invoices/${inv.id}`}
                  chevron={false}
                  title={inv.clientName ?? 'Client'}
                  subtitle={invoiceLine(inv)}
                  trailing={
                    <span className="flex flex-col items-end gap-[5px]">
                      <span className={cn('tnum text-body font-medium', inv.status === 'paid' && 'text-[var(--text-muted)]')}>
                        {money(inv.totalAmount)}
                      </span>
                      <StatusPill status={inv.status} overdue={overdue} />
                    </span>
                  }
                />
              </SwipeRow>
            );
          })}
        </Group>
      )}
      {list.data && list.data.totalCount > 100 && (
        <p className="text-center text-caption text-[var(--text-muted)]">Showing the latest 100. Search to find older invoices.</p>
      )}

      <NewInvoiceSheet open={creating} onClose={() => setCreating(false)} />
    </Screen>
  );
}

